import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  CartItem,
  CartItemDesign,
  CartItemLogo,
  CartSummary,
  CartSummaryPrice,
} from "../presentation/screens/order-create/models/CartItem";
import {
  collectCartLogoIds,
  patchStorageCounts,
} from "../presentation/screens/order-confirmation/storageVerify";
import { VerifyStorageRowDto } from "../data/models/responses/VerifyStorageResponseDto";
import { useStorageRefreshOnAddToCart } from "../hooks/useStorageRefreshOnAddToCart";
import {
  clearCartPriceCheckUpdated,
  useCartPriceCheckOnAdd,
} from "../hooks/useCartPriceCheck";
import { NotImplemented } from "../presentation/shared/functions/not-implemented.function";
import {
  trackAddToCart,
  trackRemoveFromCart,
  trackCartCleared,
  trackErrorShown,
} from "../utils/functions/analytics";
import { useRepositoriesContext } from "./RepositoriesContext";
import { useProfileContext } from "./ProfileContext";
import { useOrganizationImpersonationContext } from "./OrganizationImpersonationContext";
import { getActAsOrgId } from "./actAsStore";
import { parseJSONWithFallback } from "../utils/functions/JsonParse";
import { Order } from "../core/models/entities/Order";
import { LogoType } from "../core/models/entities/BaseLogo";
import {
  getProductPrice,
  getDesignProductPrice,
} from "../core/models/entities/BaseLogo";
import { Price } from "../core/models/entities/Price";
import { useCartSync } from "../hooks/useCartSync";
import { useErrorToast } from "../presentation/components/Toast/Toast";
import { Role } from "../core/models/entities/Role";
import { Bounce, toast } from "react-toastify";
import i18next from "i18next";
import { enviroment } from "../enviroment";
import {
  CART_STORAGE_QUOTA_I18N_KEY,
  CART_STORAGE_QUOTA_TOAST_ID,
  describeCartStorageQuota,
  deserializeCartFromStorage,
  isStorageQuotaError,
  serializeCartForStorage,
} from "../utils/cartStorage";
import {
  cancelShadowLoad,
  cartStorageFallbackOn,
  CartStorageFallbackHost,
  clearBucketShadow,
  onLsWriteSuccess,
  OWN_SHADOW_MARKER,
  persistWithFallback,
  SHADOW_MARKER_SUFFIX,
  startShadowLoad,
} from "../utils/cartStorageFallback";
import { getCartShadowStore } from "../utils/cartShadowStore";

// Strips heavy nested data from cart items before persisting to localStorage.
// The in-memory state keeps the full objects; only the serialized version is slimmed.
function slimItemsForStorage(items: CartItem[]): CartItem[] {
  return JSON.parse(JSON.stringify(items), (key, value) => {
    // Strip recursive organization trees — cart only needs id, parentId, name, type
    if (key === "children" && Array.isArray(value)) return [];
    if (key === "parent" && value && typeof value === "object" && "navNumber" in value) return undefined;
    // Strip commission profiles from organizations
    if (key === "commission" && value && typeof value === "object" && "commissionType" in value) return undefined;
    return value;
  });
}

// Cart storage v2 (flag cartStorageV2Enabled): the ONE toast of a quota
// episode. Same look as useErrorToast (top-right, light, Bounce) plus a
// toastId, so a second call while it is still visible is a no-op, and
// autoClose off - the user has to read the instruction; the episode ref in
// the provider is what stops the nagging, not a timer. i18next.t directly
// (not the hook): this runs inside a setTimeout callback, as Toast.tsx does
// for the supplier toast. Never called with the flag off.
function showCartStorageQuotaToast(): void {
  if (toast.isActive(CART_STORAGE_QUOTA_TOAST_ID)) return;
  trackErrorShown();
  toast.error(i18next.t(CART_STORAGE_QUOTA_I18N_KEY), {
    toastId: CART_STORAGE_QUOTA_TOAST_ID,
    position: "top-right",
    autoClose: false,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "light",
    transition: Bounce,
  });
}

// Helper function to calculate startup costs with staircase pricing
function getStartupCostPrice(
  feePrices: Price[],
  quantity: number,
): { adjustedPrice: number; adjustedCustomerPrice?: number } {
  if (!feePrices || feePrices.length === 0) {
    return { adjustedPrice: 0 };
  }

  // Sort prices by minQuantity in descending order to find the appropriate price tier
  const sortedPrices = [...feePrices].sort(
    (a, b) => b.minQuantity - a.minQuantity,
  );

  // Find the first price where quantity >= minQuantity
  const applicablePrice = sortedPrices.find((price) => {
    const isApplicable = quantity >= price.minQuantity;
    return isApplicable;
  });

  // If no applicable price found, use the highest minQuantity price (lowest quantity tier)
  const priceToUse = applicablePrice || sortedPrices[sortedPrices.length - 1];

  return {
    adjustedPrice: priceToUse?.adjustedPrice || 0,
    adjustedCustomerPrice: priceToUse?.adjustedCustomerPrice,
  };
}

import {
  HeatingOption,
  ExtraLogo,
} from "../presentation/screens/order-confirmation/dialogs/add-heating-fee-order-dialog";

// Frontend cart summary calculation functions
const calculateCartSummary = (
  items: CartItem[],
  role: Role | undefined,
  heatingOption: HeatingOption = HeatingOption.none,
  extraLogos: ExtraLogo[] = [],
): CartSummary => {
  const prices: CartSummaryPrice[] = [];
  let totalQuantity = 0;
  let totalPrice = 0;
  let totalUnitPrice = 0;

  const isCustomer = role !== Role.Vendor;

  const getEffectivePrice = (
    adjustedPrice: number,
    adjustedCustomerPrice?: number | null,
  ) => {
    if (
      isCustomer &&
      adjustedCustomerPrice !== undefined &&
      adjustedCustomerPrice !== null &&
      adjustedCustomerPrice > 0
    ) {
      return adjustedCustomerPrice;
    }
    return adjustedPrice;
  };

  // Track storage usage across items to prevent double counting
  const logoStorageUsage = new Map<number, number>();

  items.forEach((item) => {
    if (item.type === "Logo") {
      const logoItem = item as CartItemLogo;

      // Calculate quantity based on logo type
      let deliveryQty = 0;
      let storageQty = 0;

      switch (logoItem.baseLogo.logoType) {
        case LogoType.ImageLogo:
        case LogoType.TextLogo:
        case LogoType.SymbolLogo:
          // Static logos: simple quantity calculation. Coerce null/undefined
          // to 0 so a missing bucket doesn't poison `deliveryQty + storageQty`
          // with NaN and silently skip the price/fee staircase below.
          deliveryQty = logoItem.toDelivery ?? 0;
          storageQty = logoItem.toStorage ?? 0;
          break;

        case LogoType.NumberLogo:
          // Number logos: calculate total quantity from delivery and storage arrays
          // For each number entry, multiply quantity by number of digits in the label
          deliveryQty =
            logoItem.deliveryQuantities?.reduce((sum, q) => {
              return sum + q.quantity * (q.label?.length || 1);
            }, 0) || 0;

          storageQty =
            logoItem.storageQuantities?.reduce((sum, q) => {
              return sum + q.quantity * (q.label?.length || 1);
            }, 0) || 0;
          break;

        case LogoType.NameLogo:
          // Name logos: count as 1 item, quantity comes from quantity selectors
          deliveryQty =
            logoItem.deliveryQuantities?.reduce(
              (sum, q) => sum + q.quantity,
              0,
            ) || 0;

          storageQty =
            logoItem.storageQuantities?.reduce(
              (sum, q) => sum + q.quantity,
              0,
            ) || 0;
          break;
      }

      const itemQuantity = deliveryQty + storageQty;

      // Storage Check Logic
      const logoId = logoItem.baseLogo.id;
      const currentUsage = logoStorageUsage.get(logoId) ?? 0;
      const inStorage = logoItem.baseLogo.inStorage ?? 0;
      const remainingStorage = inStorage - currentUsage;

      // If remaining storage covers the delivery request, it's considered "free" (0 cost)
      // We only check against deliveryQty because storageQty implies new production
      const isCoveredByStorage = remainingStorage >= deliveryQty;

      const chargeableDeliveryQty = isCoveredByStorage ? 0 : deliveryQty;
      const chargeableQuantity = chargeableDeliveryQty + storageQty;

      if (isCoveredByStorage) {
        logoStorageUsage.set(logoId, currentUsage + deliveryQty);
      }

      let itemPrice = 0;

      // Calculate price using price staircase
      if (logoItem.baseLogo.product?.prices && chargeableQuantity > 0) {
        const productPrice = getProductPrice(
          logoItem.baseLogo.product.prices,
          chargeableQuantity,
        );
        if (productPrice) {
          const effectiveUnitPrice = getEffectivePrice(
            productPrice.adjustedPrice,
            productPrice.adjustedCustomerPrice,
          );
          itemPrice = effectiveUnitPrice * chargeableQuantity;
          totalUnitPrice += effectiveUnitPrice;

          prices.push({
            productNo: logoItem.baseLogo.product?.sku || "",
            productName: logoItem.title,
            productType: "Logo",
            relatedTo: "",
            toStorage: logoItem.toStorage,
            toDelivery: logoItem.toDelivery,
            price: itemPrice,
            unitPrice: productPrice.adjustedPrice,
            customerPrice: productPrice.adjustedCustomerPrice,
          });
        }
      }

      totalQuantity += itemQuantity;
      totalPrice += itemPrice;

      // Add additional fees
      if (logoItem.baseLogo.product?.additionalFees && chargeableQuantity > 0) {
        logoItem.baseLogo.product.additionalFees.forEach((fee) => {
          let feeTotal = 0;
          let unitPriceForSummary = fee.unitPrice; // Default to original unit price
          let customerPriceForSummary: number | undefined = undefined;
          let effectiveUnitPrice = fee.unitPrice;

          // Special handling for startup costs with quantity-dependent pricing
          if (
            fee.name.toLowerCase().includes("startup") ||
            fee.name.toLowerCase().includes("start-up") ||
            fee.name.toLowerCase().includes("opstart")
          ) {
            // For startup costs, use staircase pricing based on the total item quantity
            const startupPrice = getStartupCostPrice(
              fee.prices,
              chargeableQuantity,
            );
            // Multiply by the actual number of colors in the logo
            const numberOfColors = logoItem.baseLogo.colors?.length || 1;
            effectiveUnitPrice = getEffectivePrice(
              startupPrice.adjustedPrice,
              startupPrice.adjustedCustomerPrice,
            );
            feeTotal = effectiveUnitPrice * numberOfColors;
            unitPriceForSummary = startupPrice.adjustedPrice; // Use the calculated price per color as unit price
            customerPriceForSummary = startupPrice.adjustedCustomerPrice;
          } else {
            // For regular fees (like film charges), use fixed pricing
            // Try to find customer price if available in prices array
            if (fee.prices && fee.prices.length > 0) {
              const feePrice = getProductPrice(fee.prices, fee.quantity || 1);
              if (feePrice) {
                customerPriceForSummary = feePrice.adjustedCustomerPrice;
                effectiveUnitPrice = getEffectivePrice(
                  fee.unitPrice,
                  feePrice.adjustedCustomerPrice,
                );
              }
            }
            feeTotal = effectiveUnitPrice * fee.quantity;
          }

          totalPrice += feeTotal;

          prices.push({
            productNo: fee.itemNo,
            productName: fee.name,
            productType: "Additional Fee",
            relatedTo: logoItem.baseLogo.product?.sku || "",
            toStorage: 0,
            toDelivery: 0,
            price: feeTotal,
            unitPrice: unitPriceForSummary,
            customerPrice: customerPriceForSummary,
          });
        });
      }
    } else if (item.type === "Design") {
      const designItem = item as CartItemDesign;

      // Designs: sum quantities from all instances
      const itemQuantity = designItem.instances.reduce((sum, instance) => {
        return sum + (instance.toDelivery ?? 0) + (instance.toStorage ?? 0);
      }, 0);
      let itemPrice = 0;

      // Calculate design price
      if (designItem.design.product?.totalPrices && itemQuantity > 0) {
        const designPrice = getDesignProductPrice(
          designItem.design.product.totalPrices,
          itemQuantity,
        );
        if (designPrice) {
          const effectiveUnitPrice = getEffectivePrice(
            designPrice.adjustedPrice,
            designPrice.adjustedCustomerPrice,
          );
          itemPrice = effectiveUnitPrice * itemQuantity;
          totalUnitPrice += effectiveUnitPrice;

          prices.push({
            productNo: designItem.design.product?.mul || "",
            productName: designItem.title,
            productType: "Design",
            relatedTo: "",
            toStorage: 0,
            toDelivery: 0,
            price: itemPrice,
            unitPrice: designPrice.adjustedPrice,
            customerPrice: designPrice.adjustedCustomerPrice,
          });
        }
      }

      // Calculate heating fees if applicable
      // Only apply if heating option is ALL, or if PARTLY and this design/logo is selected
      // For now, handling 'ALL' case for designs
      if (
        heatingOption === HeatingOption.all &&
        designItem.design.heating?.prices
      ) {
        // Heating is applied per instance-logo
        // So we need to sum up (quantity of each instance * number of logos in that instance)
        // Wait, heating usually refers to the *application* of logos onto the garment.
        // If "heating" means "heat transfer application", it's typically per logo application.
        // Let's check how it's done in useOrderActionHandler.ts -> applyHeatingOption
        // It sums up: instQty * inst.logos.length.

        const heatingQty = designItem.instances.reduce((total, inst) => {
          const instQty = (inst.toDelivery ?? 0) + (inst.toStorage ?? 0);
          const instanceMultiplier = inst.logos.length;
          return total + instQty * instanceMultiplier;
        }, 0);

        if (heatingQty > 0) {
          const heatingPrice = getProductPrice(
            designItem.design.heating.prices,
            heatingQty,
          );
          if (heatingPrice) {
            const effectiveUnitPrice = getEffectivePrice(
              heatingPrice.adjustedPrice,
              heatingPrice.adjustedCustomerPrice,
            );
            const heatingTotal = effectiveUnitPrice * heatingQty;
            totalPrice += heatingTotal;
            prices.push({
              productNo: designItem.design.heating.sku,
              productName: designItem.design.heating.name,
              productType: "Heating",
              relatedTo: designItem.title,
              toStorage: 0,
              toDelivery: 0,
              price: heatingTotal,
              unitPrice: heatingPrice.adjustedPrice,
              customerPrice: heatingPrice.adjustedCustomerPrice,
            });
          }
        }
      }

      // Calculate fees for logos in design
      if (designItem.design.designLogos) {
        designItem.design.designLogos.forEach((designLogo) => {
          const baseLogo = designLogo.logo;
          if (!baseLogo) return;

          const logoId = baseLogo.id;
          const quantityNeeded = itemQuantity;

          const currentUsage = logoStorageUsage.get(logoId) ?? 0;
          const inStorage = baseLogo.inStorage ?? 0;
          const remainingStorage = inStorage - currentUsage;

          const isCoveredByStorage = remainingStorage >= quantityNeeded;

          if (isCoveredByStorage) {
            logoStorageUsage.set(logoId, currentUsage + quantityNeeded);
            // No fees if covered by storage
            return;
          }

          // Not covered: Add fees
          if (baseLogo.product?.additionalFees) {
            baseLogo.product.additionalFees.forEach((fee) => {
              let feeTotal = 0;
              let unitPriceForSummary = fee.unitPrice;
              let customerPriceForSummary: number | undefined = undefined;
              let effectiveUnitPrice = fee.unitPrice;

              if (
                fee.name.toLowerCase().includes("startup") ||
                fee.name.toLowerCase().includes("start-up") ||
                fee.name.toLowerCase().includes("opstart")
              ) {
                const startupPrice = getStartupCostPrice(
                  fee.prices,
                  quantityNeeded,
                );
                const numberOfColors = baseLogo.colors?.length || 1;
                effectiveUnitPrice = getEffectivePrice(
                  startupPrice.adjustedPrice,
                  startupPrice.adjustedCustomerPrice,
                );
                feeTotal = effectiveUnitPrice * numberOfColors;
                unitPriceForSummary = startupPrice.adjustedPrice;
                customerPriceForSummary = startupPrice.adjustedCustomerPrice;
              } else {
                // Try to find customer price if available in prices array
                if (fee.prices && fee.prices.length > 0) {
                  const feePrice = getProductPrice(
                    fee.prices,
                    fee.quantity || 1,
                  );
                  if (feePrice) {
                    customerPriceForSummary = feePrice.adjustedCustomerPrice;
                    effectiveUnitPrice = getEffectivePrice(
                      fee.unitPrice,
                      feePrice.adjustedCustomerPrice,
                    );
                  }
                }
                feeTotal = effectiveUnitPrice * fee.quantity;
              }

              totalPrice += feeTotal;

              prices.push({
                productNo: fee.itemNo,
                productName: fee.name,
                productType: "Additional Fee",
                relatedTo: designItem.title,
                toStorage: 0,
                toDelivery: 0,
                price: feeTotal,
                unitPrice: unitPriceForSummary,
                customerPrice: customerPriceForSummary,
              });
            });
          }
        });
      }

      totalQuantity += itemQuantity;
      totalPrice += itemPrice;
    }
  });

  return {
    prices,
    totalQuantity,
    totalPrice,
    totalUnitPrice,
  };
};

export interface OrderBasket {
  items: CartItem[];
  logos: CartItemLogo[];
  designs: CartItemDesign[];
  cartSummary?: CartSummary;
  isUpdating: boolean;
  addLogoItem: (item: CartItemLogo) => Promise<CartItemLogo>;
  addLogoItems: (items: CartItemLogo[]) => Promise<CartItemLogo[]>;
  addDesignItem: (item: CartItemDesign) => Promise<CartItemDesign>;
  addDesignItems: (items: CartItemDesign[]) => Promise<CartItemDesign[]>;
  addItems: (items: CartItem[]) => Promise<CartItem[]>;
  updateItem: (item: CartItem) => Promise<CartItem>;
  removeItem: (id: string) => void;
  // `scope` is the cartScope the caller captured when its flow started (e.g.
  // at order placement). If the act-as identity moved while the flow was in
  // flight, the clear lands in that identity's parked bucket instead of the
  // in-memory cart, which by then belongs to the incoming identity.
  //
  // `persistNow` (default false) additionally flushes the clear to
  // localStorage SYNCHRONOUSLY. Opt in ONLY when the caller leaves the
  // document right after clearing (sync Stripe checkout redirect), where the
  // debounced persist would never fire and a stale bucket would revive the
  // just-ordered items on return. Everyone else must stay on the default:
  // logout in particular relies on the debounced []-write typically NOT
  // landing before the B2C redirect, so the persisted cart survives
  // logout -> next login.
  clearItems: (scope?: number, persistNow?: boolean) => void;
  // The identity scope the live cart belongs to (act-as org id, 0 = plain
  // identity). Capture it at the start of an async flow and pass it back to
  // clearItems so a late completion can't clear the wrong identity's cart.
  cartScope: number;
  activeOrder?: Order;
  setActiveOrder: (order?: Order) => void;
  // The heating/extra-logo transformed cart items the LOCAL checkout draft
  // (order id 0) was built from. When placing that draft they must be posted
  // instead of the raw cart items so the confirmation page and the API
  // payload describe the same order. Never post them for server-fetched
  // orders, and they are invalidated by structural cart mutations.
  checkoutItems?: CartItem[];
  setCheckoutItems: (items?: CartItem[]) => void;
  // Cart sync functionality
  isCartSyncing: boolean;
  hasStaleItems: boolean;
  refreshCart: () => void;
  // Heating
  heatingOption: HeatingOption;
  setHeatingOption: (option: HeatingOption) => void;
  extraLogos: ExtraLogo[];
  setExtraLogos: (logos: ExtraLogo[]) => void;
}

const CartContext = createContext<OrderBasket | undefined>(undefined);

interface CartProviderProps {
  children: React.ReactNode;
}

const CartProvider = ({ children }: CartProviderProps): React.ReactElement => {
  const { b2CId, organizationId, role, organization, workshopMode } =
    useProfileContext();
  // Act-as-vendor: the persisted cart is keyed on the ADMIN's own b2CId even
  // while impersonating (the profile always returns the caller's b2CId), so
  // the key carries the identity SCOPE (act-as org id, 0 = plain identity).
  // Each identity parks its cart in its own bucket: switching identities
  // never clears or leaks a cart, it swaps which bucket is live.
  const { organizationId: actAsOrgId } = useOrganizationImpersonationContext();

  const cartHidden =
    (role === Role.EndUser && organization?.hidePrices === true) ||
    workshopMode;

  const cartScope = actAsOrgId ?? 0;
  // Keeps the `_products` suffix: RouteErrorView's reset-cart escape hatch
  // clears by endsWith("_products") and must keep matching these buckets.
  const productsKey = `${b2CId}_${cartScope}_products`;

  const [items, setItems] = useState<CartItem[]>(() => {
    // getActAsOrgId (module store) instead of the context value: the lazy
    // initializer must not depend on provider mounting order, and the module
    // store is synced synchronously with every impersonation change.
    const stored = localStorage.getItem(
      `${b2CId}_${getActAsOrgId() ?? 0}_products`,
    );
    // Cart storage v2 (flag): the same bucket read through the v2
    // deserializer - instance logos are rehydrated from the design's own
    // copy; a v1 bucket reads exactly as today. Off = today's parse.
    if (enviroment.cartStorageV2Enabled) return deserializeCartFromStorage(stored);
    return stored ? parseJSONWithFallback(stored, []) : [];
  });
  const [activeOrder, setActiveOrder] = useState<Order | undefined>(undefined);
  const [checkoutItems, setCheckoutItems] = useState<CartItem[] | undefined>(
    undefined,
  );

  const [cartSummary, setCartSummary] = useState<CartSummary>();
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  // Heating State
  const [heatingOption, setHeatingOption] = useState<HeatingOption>(
    HeatingOption.none,
  );
  const [extraLogos, setExtraLogos] = useState<ExtraLogo[]>([]);

  // The scope the CURRENT in-memory items belong to. The debounced persist
  // derives its target key from this at schedule time, so a flush that fires
  // around an identity switch always lands in the bucket the items came from
  // — never in the incoming identity's bucket.
  const itemsScopeRef = useRef<number>(cartScope);
  const persistTimerRef = useRef<number | undefined>(undefined);
  // Cart storage v2 (flag): true from the first QuotaExceeded until the
  // next SUCCESSFUL write - one toast per episode, and no new toast after
  // the user closed it until a write has succeeded and failed again. Never
  // read with the flag off.
  const quotaEpisodeRef = useRef<boolean>(false);
  // The write the debounced persist has scheduled but not yet flushed.
  // ProfileContext's loading gate can unmount this provider in the SAME
  // commit as an act-as enter with a cold profile cache — before the
  // scope-switch effect can park the cart — so the unmount flush below needs
  // the payload outside the effect closure.
  const pendingPersistRef = useRef<
    { key: string; items: CartItem[] } | undefined
  >(undefined);
  // Cart storage fallback (flag cartStorageFallbackOn, design
  // DESIGN-CART-STORAGE-TRIN3): the refs the React-free core in
  // utils/cartStorageFallback shares with this provider. quotaEpisodeRef
  // keeps its v2 meaning; toastShownRef gates the one toast per episode
  // under the flag. None of them is read with the flag off.
  const reclaimedRef = useRef<boolean>(false);
  const shadowMayExistRef = useRef<boolean>(false);
  const structuralGenRef = useRef<number>(0);
  const mountedRef = useRef<boolean>(false);
  const toastShownRef = useRef<boolean>(false);
  const fallbackHostRef = useRef<CartStorageFallbackHost | undefined>(
    undefined,
  );
  // Built on first use (never with the flag off - the IndexedDB store object
  // is only created here, and it opens nothing until a shadow is needed).
  const fallbackHost = (): CartStorageFallbackHost => {
    if (!fallbackHostRef.current) {
      fallbackHostRef.current = {
        storage: localStorage,
        store: getCartShadowStore(),
        b2CId: b2CId ?? "",
        quotaEpisodeRef,
        reclaimedRef,
        shadowMayExistRef,
        structuralGenRef,
        mountedRef,
        itemsScopeRef,
        toastShownRef,
        setItems,
        toastOnce: () => {
          if (toastShownRef.current) return;
          toastShownRef.current = true;
          showCartStorageQuotaToast();
        },
      };
    }
    fallbackHostRef.current.b2CId = b2CId ?? "";
    return fallbackHostRef.current;
  };

  const { orderRepository } = useRepositoriesContext();

  // Stock look-up on add-to-cart (flag storageRefreshOnAddToCartEnabled):
  // the answer lands in the cart's own copies of the logos - standalone
  // baseLogo, a design's designLogos[].logo and its instances' baseLogo -
  // through the same patch the checkout verify uses, on the items of THAT
  // moment (functional update). Only when the live cart still belongs to
  // the identity the call was made for; not a structural change, so the
  // checkout snapshot is left alone, exactly like updateInCart.
  const applyStorageRows = useCallback(
    (rows: VerifyStorageRowDto[], scope: number) => {
      if (scope !== itemsScopeRef.current) return;
      setItems((state) => patchStorageCounts(state, rows));
    },
    [],
  );
  const refreshStorageOnAdd = useStorageRefreshOnAddToCart(applyStorageRows);
  // Cart price check (flag cartPriceCheckEnabled): ONE fire-and-forget POST
  // per logo just added, never awaited - the line is in the cart before it
  // leaves. The live items are handed to the hook so a status poll can stop
  // when its logo left the cart and name the cart entry to re-read; the
  // fresh prices land through useCartSync below, like every other sync.
  // A logo's earlier "Price updated" note is dropped by the hook itself,
  // and only when a NEW call leaves (a re-add inside the cooldown sends
  // nothing and keeps the note - the line's price is still the updated one).
  const requestPriceCheckOnAdd = useCartPriceCheckOnAdd(items);

  useEffect(() => {
    if (!b2CId) return;

    // One-time migration from the pre-scoped layout (`${b2CId}_products` plus
    // an `_actas` marker naming the act-as org the cart was built under) into
    // the scoped bucket that identity owns today. Never overwrite an existing
    // scoped bucket — it is newer by definition, so the legacy copy just goes.
    const legacyProductsKey = `${b2CId}_products`;
    const legacyMarkerKey = `${b2CId}_products_actas`;
    const legacy = localStorage.getItem(legacyProductsKey);
    let legacyMigrated = true;
    if (legacy != null) {
      const marker = localStorage.getItem(legacyMarkerKey);
      const legacyScope = marker && /^\d+$/.test(marker) ? marker : "0";
      const targetKey = `${b2CId}_${legacyScope}_products`;
      if (localStorage.getItem(targetKey) == null) {
        try {
          localStorage.setItem(targetKey, legacy);
        } catch (error) {
          // The copy briefly doubles the cart's storage footprint, so a big
          // legacy cart near the quota can throw here. Throwing out of the
          // effect would crash boot into the route error boundary — instead
          // keep the legacy key (the only copy) and retry on the next boot,
          // after the cleanup below has freed space.
          legacyMigrated = false;
          console.error("Failed to migrate legacy cart", error);
        }
      }
    }
    if (legacyMigrated) {
      localStorage.removeItem(legacyProductsKey);
      localStorage.removeItem(legacyMarkerKey);
    }

    // Cleanup old localStorage keys to free up space. This user's scoped
    // buckets are PARKED carts (one per identity) and must all survive —
    // everything else (other users' buckets, stray legacy/marker forms) goes.
    const ownScopedProductsKey = new RegExp(`^${b2CId}_\\d+_products$`);
    const currentActiveOrderKey = `${b2CId}_activeOrder`;
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.endsWith("_products") ||
          key.endsWith("_activeOrder") ||
          key.endsWith("_products_actas") ||
          // Cart storage fallback (flag): other users' shadow markers go
          // too; this user's own markers survive with their buckets.
          (cartStorageFallbackOn && key.endsWith(SHADOW_MARKER_SUFFIX))) &&
        !ownScopedProductsKey.test(key) &&
        !(cartStorageFallbackOn && OWN_SHADOW_MARKER(b2CId).test(key)) &&
        key !== currentActiveOrderKey &&
        // A quota-failed migration keeps these for a retry on the next boot;
        // after a successful migration they are already gone.
        key !== legacyProductsKey &&
        key !== legacyMarkerKey
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }, [b2CId]);

  // Identity switch (act-as enter/exit — including the automatic reset paths
  // in ProfileContext and OrganizationImpersonationContext, which also just
  // change the act-as org id): park the outgoing identity's cart in its own
  // bucket and load the incoming identity's parked bucket. Nothing is cleared
  // and nothing crosses over; this effect is the ONLY switch point (the
  // banner/dialog no longer touch the cart). Guarded on itemsScopeRef so it
  // is a no-op on mount and under StrictMode's double invocation.
  useEffect(() => {
    if (itemsScopeRef.current === cartScope) return;

    // `items` still holds the OUTGOING identity's cart in this commit (the
    // persist effect below did not re-run — its deps exclude the scope).
    // Flush it to the outgoing bucket now and cancel the debounced write so
    // the last edits are parked rather than lost or written cross-scope.
    window.clearTimeout(persistTimerRef.current);
    pendingPersistRef.current = undefined;
    const outgoingKey = `${b2CId}_${itemsScopeRef.current}_products`;
    // Cart storage fallback (flag): a shadow load for the outgoing key must
    // not land on the incoming identity's items.
    if (cartStorageFallbackOn) cancelShadowLoad(outgoingKey);
    let parked: string | undefined;
    try {
      if (enviroment.cartStorageV2Enabled) {
        // Cart storage v2 (flag): park in the slim format. No toast here
        // (as today) - only the console.error below.
        parked = serializeCartForStorage(items);
        localStorage.setItem(outgoingKey, parked);
        quotaEpisodeRef.current = false;
        if (cartStorageFallbackOn) onLsWriteSuccess(fallbackHost(), outgoingKey, parked);
      } else {
        localStorage.setItem(
          outgoingKey,
          JSON.stringify(slimItemsForStorage(items)),
        );
      }
    } catch (error) {
      console.error("Failed to park cart on identity switch", error);
      // Cart storage fallback (flag): layer 1 + 2 for the outgoing bucket,
      // still without a toast.
      if (cartStorageFallbackOn && parked !== undefined) {
        persistWithFallback(fallbackHost(), outgoingKey, parked, error, false);
      }
    }

    itemsScopeRef.current = cartScope;
    const stored = localStorage.getItem(productsKey);
    setItems(
      enviroment.cartStorageV2Enabled
        ? deserializeCartFromStorage(stored)
        : stored ? parseJSONWithFallback(stored, []) : [],
    );
    // Cart storage fallback (flag): the incoming bucket may have a newer
    // shadow (marker-gated: one getItem, IndexedDB only if it says so).
    if (cartStorageFallbackOn) startShadowLoad(fallbackHost(), productsKey, stored);
    // Checkout state is scope-bound: the incoming identity starts from its
    // parked cart, never from the outgoing identity's half-finished checkout.
    setActiveOrder(undefined);
    setCheckoutItems(undefined);
    setHeatingOption(HeatingOption.none);
    setExtraLogos([]);
  }, [cartScope, b2CId, items, productsKey]);

  useEffect(() => {
    if (items.length > 0) {
      setIsUpdating(true);
      const newCartSummary = calculateCartSummary(
        items,
        role,
        heatingOption,
        extraLogos,
      );
      setCartSummary(newCartSummary);
      setIsUpdating(false);
    } else {
      setCartSummary({
        prices: [],
        totalQuantity: 0,
        totalPrice: 0,
        totalUnitPrice: 0,
      });
    }
  }, [items, heatingOption, extraLogos, role]);

  // Backend cart summary calculation is no longer used
  // All calculations are now done on the frontend

  // Structural cart changes (add/remove lines) invalidate the
  // checkout-transformed snapshot: it was derived from the cart as it looked
  // when the heating dialog was confirmed. updateInCart deliberately does not
  // invalidate - the background cart sync refreshes prices through it, and a
  // price refresh while the confirmation page is open must not make the
  // confirm fall back to posting the raw, un-flattened cart.
  const invalidateCheckoutItems = (): void => {
    setCheckoutItems(undefined);
    // Cart storage fallback (flag): the same structural changes (add /
    // remove / clear - never updateInCart, which the background sync uses)
    // count up the generation a pending shadow load is checked against.
    if (cartStorageFallbackOn) structuralGenRef.current++;
  };

  async function addLogoToCart(item: CartItemLogo): Promise<CartItemLogo> {
    return new Promise<CartItemLogo>((resolve, reject) => {
      if (cartHidden) {
        reject(new Error("Cart is not available for this account."));
        return;
      }
      if (!shouldAllowPluralCustomerProductsInCart(item)) {
        reject(
          new Error(
            "You are not allowed to operate products of multiple customers at the same time.",
          ),
        );
        return;
      }

      setItems((state) => {
        const isItemInCart = state.find(
          (cartItem) => cartItem.id === item.id,
        ) as CartItemLogo;
        if (isItemInCart) {
          const newState = state.map((cartItem: CartItem) =>
            cartItem.id === item.id ? { ...item } : cartItem,
          );
          return newState;
        } else {
          return [...state, { ...item }];
        }
      });

      invalidateCheckoutItems();
      resolve(item);
      trackAddToCart("Logo", item.id);
      refreshStorageOnAdd([item]);
      requestPriceCheckOnAdd([item]);
    });
  }

  async function addLogosToCart(
    items: CartItemLogo[],
  ): Promise<CartItemLogo[]> {
    return new Promise<CartItemLogo[]>((resolve, reject) => {
      if (cartHidden) {
        reject(new Error("Cart is not available for this account."));
        return;
      }
      items.map((item) => {
        if (!shouldAllowPluralCustomerProductsInCart(item)) {
          reject(
            new Error(
              "You are not allowed to operate products of multiple customers at the same time.",
            ),
          );
          return;
        }
      });

      setItems((state) => {
        const newState = items.reduce((acc, item) => {
          const isItemInCart = acc.find(
            (cartItem) => cartItem.id === item.id,
          ) as CartItemLogo;
          if (isItemInCart) {
            return acc.map((cartItem: CartItem) =>
              cartItem.id === item.id ? { ...item } : cartItem,
            );
          } else {
            return [...acc, { ...item }];
          }
        }, state);

        return newState;
      });

      invalidateCheckoutItems();
      resolve(items);
      items.forEach((i) => trackAddToCart("Logo", i.id));
      refreshStorageOnAdd(items);
      requestPriceCheckOnAdd(items);
    });
  }

  async function addDesignToCart(
    item: CartItemDesign,
  ): Promise<CartItemDesign> {
    return new Promise<CartItemDesign>((resolve, reject) => {
      if (cartHidden) {
        reject(new Error("Cart is not available for this account."));
        return;
      }
      if (!shouldAllowPluralCustomerProductsInCart(item)) {
        reject(
          new Error(
            "You are not allowed to operate products of multiple customers at the same time.",
          ),
        );
        return;
      }

      setItems((state) => {
        const isItemInCart = state.find(
          (cartItem) => cartItem.id === item.id,
        ) as CartItemDesign;
        if (isItemInCart) {
          const newState = state.map((cartItem: CartItem) =>
            cartItem.id === item.id ? { ...item } : cartItem,
          );
          return newState;
        } else {
          return [...state, { ...item }];
        }
      });

      invalidateCheckoutItems();
      resolve(item);
      trackAddToCart("Design", item.id);
      refreshStorageOnAdd([item]);
      requestPriceCheckOnAdd([item]);
    });
  }

  async function addDesignsToCart(
    items: CartItemDesign[],
  ): Promise<CartItemDesign[]> {
    return new Promise<CartItemDesign[]>((resolve, reject) => {
      if (cartHidden) {
        reject(new Error("Cart is not available for this account."));
        return;
      }
      items.map((item) => {
        if (!shouldAllowPluralCustomerProductsInCart(item)) {
          reject(
            new Error(
              "You are not allowed to operate products of multiple customers at the same time.",
            ),
          );
          return;
        }
      });

      setItems((state) => {
        const newState = items.reduce((acc, item) => {
          const isItemInCart = acc.find(
            (cartItem) => cartItem.id === item.id,
          ) as CartItemDesign;
          if (isItemInCart) {
            return acc.map((cartItem: CartItem) =>
              cartItem.id === item.id ? { ...item } : cartItem,
            );
          } else {
            return [...acc, { ...item }];
          }
        }, state);

        return newState;
      });

      invalidateCheckoutItems();
      resolve(items);
      items.forEach((i) => trackAddToCart("Design", i.id));
      refreshStorageOnAdd(items);
      requestPriceCheckOnAdd(items);
    });
  }

  async function addItemsToCart(items: CartItem[]): Promise<CartItem[]> {
    return new Promise<CartItem[]>((resolve, reject) => {
      if (cartHidden) {
        reject(new Error("Cart is not available for this account."));
        return;
      }
      items.map((item) => {
        if (!shouldAllowPluralCustomerProductsInCart(item)) {
          reject(
            new Error(
              "You are not allowed to operate products of multiple customers at the same time.",
            ),
          );
          return;
        }
      });

      setItems((state) => {
        const newState = items.reduce((acc, item) => {
          const isItemInCart = acc.find(
            (cartItem) => cartItem.id === item.id,
          ) as CartItem;
          if (isItemInCart) {
            return acc.map((cartItem: CartItem) =>
              cartItem.id === item.id ? { ...item } : cartItem,
            );
          } else {
            return [...acc, { ...item }];
          }
        }, state);

        return newState;
      });

      invalidateCheckoutItems();
      resolve(items);
    });
  }

  async function updateInCart(item: CartItem): Promise<CartItem> {
    return new Promise<CartItem>((resolve, reject) => {
      if (!shouldAllowPluralCustomerProductsInCart(item)) {
        reject(
          new Error(
            "You are not allowed to operate products of multiple customers at the same time.",
          ),
        );
        return;
      }

      const isItemInCart = items.find((cartItem) => cartItem.id === item.id);

      if (isItemInCart) {
        setItems((currentItems) =>
          currentItems.map((cartItem: CartItem) =>
            cartItem.id === item.id ? { ...item } : cartItem,
          ),
        );
        return item;
      } else {
        switch (item.type) {
          case "Logo": {
            addLogoToCart(item as CartItemLogo)
              .then((x) => resolve(x))
              .catch((x) => reject(x));
            break;
          }
          case "Design": {
            addDesignToCart(item as CartItemDesign)
              .then((x) => resolve(x))
              .catch((x) => reject(x));
            break;
          }
        }
      }

      resolve(item);
    });
  }

  const removeFromCart = (id: string): void => {
    const next = items.filter((cartItem) => cartItem.id !== id);

    if (next.length === 0) {
      localStorage.removeItem(productsKey);
      // Cart storage fallback (flag): the marker goes with the bucket.
      if (cartStorageFallbackOn) clearBucketShadow(fallbackHost(), productsKey);
    }

    setItems(next);
    invalidateCheckoutItems();
    trackRemoveFromCart(id);
    // Cart price check: the removed line takes its "Price updated" note
    // with it - for the logos no remaining line still shows (a logo placed
    // in a kept design line, or kept as its own line, keeps its note).
    // Flag off = no-op.
    const remaining = collectCartLogoIds(next);
    clearCartPriceCheckUpdated(
      collectCartLogoIds(items.filter((cartItem) => cartItem.id === id)).filter(
        (logoId) => !remaining.includes(logoId),
      ),
    );
  };

  const clearCart = (scope?: number, persistNow: boolean = false): void => {
    // A caller-captured scope that no longer matches the live items means the
    // act-as identity switched while the caller's flow (e.g. order placement)
    // was in flight: the in-memory cart belongs to the INCOMING identity, so
    // clear the originating identity's parked bucket instead — otherwise the
    // incoming cart is wiped and the just-ordered items get revived on the
    // next switch back (double-order risk).
    if (scope !== undefined && scope !== itemsScopeRef.current) {
      localStorage.removeItem(`${b2CId}_${scope}_products`);
      // Cart storage fallback (flag): the marker goes with the bucket.
      if (cartStorageFallbackOn) {
        clearBucketShadow(fallbackHost(), `${b2CId}_${scope}_products`);
      }
      trackCartCleared();
      return;
    }
    setItems([]);
    // Cart price check: an emptied cart carries no "Price updated" notes
    // (flag off = no-op).
    clearCartPriceCheckUpdated();
    if (persistNow) {
      // Opt-in ONLY (see the interface comment): persist the clear
      // SYNCHRONOUSLY, mirroring removeFromCart's empty-cart removal and the
      // scope-switch flush. The sync Stripe checkout redirect does
      // window.location.assign immediately after clearItems, so the debounced
      // persist effect would never fire and a stale non-empty bucket would
      // revive the just-ordered items on return from Stripe (double-order
      // risk). Cancel any pending debounced write too, so it can't resurrect
      // the old items after this removal. All other callers (logout,
      // handleChangeOrder) keep the historical debounced behavior — logout
      // depends on the []-write typically NOT landing before the B2C
      // redirect, so the cart survives logout -> next login.
      window.clearTimeout(persistTimerRef.current);
      pendingPersistRef.current = undefined;
      localStorage.removeItem(`${b2CId}_${itemsScopeRef.current}_products`);
      // Cart storage fallback (flag): the marker goes SYNCHRONOUSLY with
      // the bucket, in this same tick, before the Stripe redirect - so no
      // shadow can revive the just-ordered items on return. No await.
      if (cartStorageFallbackOn) {
        clearBucketShadow(
          fallbackHost(),
          `${b2CId}_${itemsScopeRef.current}_products`,
        );
      }
    }
    invalidateCheckoutItems();
    trackCartCleared();
  };

  function getCustomerId(items: CartItem[]): number | undefined {
    const firstLogo = items.find((x) => x.type === "Logo") as CartItemLogo;
    const firstDesign = items.find(
      (x) => x.type === "Design",
    ) as CartItemDesign;

    if (firstLogo) {
      return firstLogo.baseLogo.customer.parentId;
    }

    if (firstDesign) {
      return firstDesign.design.customer.parentId;
    }

    return undefined;
  }

  function shouldAllowPluralCustomerProductsInCart(item: CartItem): boolean {
    // Disable plural guard for now, as it doesn't align with business logic.
    return true;

    const customerId = getCustomerId(items);

    if (customerId === undefined) {
      return true;
    }

    switch (item.type) {
      case "Logo":
        return (item as CartItemLogo).baseLogo.customer.parentId === customerId;
      case "Design":
        return (item as CartItemDesign).design.customer.parentId === customerId;
      default:
        throw NotImplemented("OrderLine type not implemented");
    }
  }

  function handleSetActiveOrder(order?: Order) {
    setActiveOrder(order);
  }

  // Initialize cart synchronization to keep logo prices up-to-date.
  //
  // The updateItem wrapper drops cross-scope writes: on the identity-switch
  // commit, cart sync's auto-sync effect still closes over the OUTGOING
  // identity's items but already reads the INCOMING identity's price cache
  // (the act-as module store is updated synchronously before render). The
  // scope-switch effect above runs first in that flush and moves
  // itemsScopeRef, so a mismatch with the scope captured at render marks the
  // sync as stale — applying it would overwrite the incoming identity's line
  // with the outgoing identity's quantities whenever the two carts share a
  // product id.
  const itemsScopeAtRender = itemsScopeRef.current;
  const {
    isRefreshing: isCartSyncing,
    hasStaleItems,
    refreshCart,
  } = useCartSync({
    items,
    updateItem: (item) =>
      itemsScopeAtRender === itemsScopeRef.current
        ? updateInCart(item)
        : Promise.resolve(item),
  });

  useEffect(() => {
    // Debounce persistence so rapid keystrokes don't run a 1MB+ deep-clone +
    // localStorage write on every input change. Cart is restored from
    // localStorage on page load, so a short delay before persisting is fine.
    //
    // The target key is captured at SCHEDULE time from the scope the items
    // belong to (itemsScopeRef, deliberately not the render scope): a pending
    // flush must never write one identity's items into another identity's
    // bucket. The scope-switch effect above cancels this timer before the
    // scope can move, so a late flush can only ever hit the old key.
    const targetKey = `${b2CId}_${itemsScopeRef.current}_products`;
    pendingPersistRef.current = { key: targetKey, items };
    const timeoutId = window.setTimeout(() => {
      pendingPersistRef.current = undefined;
      // Cart storage v2 (flag): the payload length, for the quota
      // diagnostic below and the per-persist console line (the only FE
      // telemetry there is for the dev measurement gate).
      let payloadLength = 0;
      let payload: string | undefined;
      try {
        if (enviroment.cartStorageV2Enabled) {
          payload = serializeCartForStorage(items);
          payloadLength = payload.length;
          localStorage.setItem(targetKey, payload);
          quotaEpisodeRef.current = false;
          console.debug(
            "[cart-storage] wrote %d chars to %s",
            payloadLength,
            targetKey,
          );
          // Cart storage fallback (flag): a shadow, if any, is superseded
          // (or, during a pending load, its marker follows the bucket).
          if (cartStorageFallbackOn) onLsWriteSuccess(fallbackHost(), targetKey, payload);
        } else {
          const slimmed = slimItemsForStorage(items);
          localStorage.setItem(targetKey, JSON.stringify(slimmed));
        }
      } catch (error: any) {
        console.error("Failed to save cart to localStorage", error);
        if (enviroment.cartStorageV2Enabled) {
          // ONE toast per quota episode (see quotaEpisodeRef); every failed
          // write still gets its console diagnostic - the payload that
          // failed and how much this user's OTHER parked identity buckets
          // hold of the shared quota.
          if (isStorageQuotaError(error)) {
            const diagnostic = describeCartStorageQuota(
              localStorage,
              b2CId,
              targetKey,
              payloadLength,
              items,
            );
            console.warn(
              "[cart-storage] quota exceeded: %d chars for %s (%d lines, %d instances); %d other parked buckets hold %d chars",
              diagnostic.chars,
              diagnostic.key,
              diagnostic.lines,
              diagnostic.instances,
              diagnostic.parkedBuckets,
              diagnostic.parkedChars,
            );
            if (cartStorageFallbackOn && payload !== undefined) {
              // Cart storage fallback (flag): layer 1 (re-slim own parked
              // buckets, retry) then layer 2 (IndexedDB shadow); the toast
              // only when both fail, once per episode.
              persistWithFallback(fallbackHost(), targetKey, payload, error, true);
            } else if (!quotaEpisodeRef.current) {
              quotaEpisodeRef.current = true;
              showCartStorageQuotaToast();
            }
          }
        } else if (
          error.name === "QuotaExceededError" ||
          error.name === "NS_ERROR_DOM_QUOTA_REACHED"
        ) {
          useErrorToast(
            "Storage quota exceeded. Your cart changes may not be saved.",
          );
        }
      }
    }, 400);
    persistTimerRef.current = timeoutId;

    return () => window.clearTimeout(timeoutId);
  }, [items, b2CId]);

  useEffect(() => {
    return () => {
      // Unmount flush for the debounced persist above: ProfileContext's
      // loading gate unmounts this provider in the same commit as an act-as
      // enter with a cold profile cache — before the scope-switch effect can
      // park the cart — and the persist cleanup then cancels the pending
      // write. Flush it synchronously so the last ≤400 ms of edits land in
      // the bucket they were scheduled for instead of being lost.
      const pending = pendingPersistRef.current;
      if (!pending) return;
      pendingPersistRef.current = undefined;
      let flushed: string | undefined;
      try {
        if (enviroment.cartStorageV2Enabled) {
          // Cart storage v2 (flag): flush in the slim format; no toast (as
          // today).
          flushed = serializeCartForStorage(pending.items);
          localStorage.setItem(pending.key, flushed);
          if (cartStorageFallbackOn) onLsWriteSuccess(fallbackHost(), pending.key, flushed);
        } else {
          localStorage.setItem(
            pending.key,
            JSON.stringify(slimItemsForStorage(pending.items)),
          );
        }
      } catch (error) {
        console.error("Failed to flush cart on unmount", error);
        // Cart storage fallback (flag): the last <= 400 ms of edits still
        // reach the shadow of the bucket they were scheduled for; no toast.
        if (cartStorageFallbackOn && flushed !== undefined) {
          persistWithFallback(fallbackHost(), pending.key, flushed, error, false);
        }
      }
    };
  }, []);


  useEffect(() => {
    // Mount-time re-read of the current identity's bucket: runs AFTER the
    // legacy-migration effect above (definition order), so a cart migrated
    // into this scope's bucket on this very mount is picked up immediately —
    // the useState initializer ran before the migration could move it.
    const cartItems = localStorage.getItem(productsKey);
    if (cartItems) {
      setItems(
        enviroment.cartStorageV2Enabled
          ? deserializeCartFromStorage(cartItems)
          : parseJSONWithFallback(cartItems, []),
      );
    }
  }, []);

  // Cart storage fallback (flag): defined AFTER the mount re-read above, so
  // the legacy migration has run and the bucket read here is the migrated
  // one. Marker-gated: one getItem; IndexedDB is touched only when a marker
  // says a newer shadow exists for this bucket (0 calls for everyone else).
  useEffect(() => {
    if (!cartStorageFallbackOn || !b2CId) return;
    mountedRef.current = true;
    reclaimedRef.current = false;
    startShadowLoad(fallbackHost(), productsKey, localStorage.getItem(productsKey));
    return () => {
      mountedRef.current = false;
    };
  }, [b2CId]);

  return (
    <CartContext.Provider
      value={{
        items: items,
        cartSummary,
        isUpdating,
        logos: items.filter((x) => x.type === "Logo") as CartItemLogo[],
        designs: items.filter((x) => x.type === "Design") as CartItemDesign[],
        addLogoItem: addLogoToCart,
        addLogoItems: addLogosToCart,
        addDesignItem: addDesignToCart,
        addDesignItems: addDesignsToCart,
        addItems: addItemsToCart,
        updateItem: updateInCart,
        removeItem: removeFromCart,
        clearItems: clearCart,
        cartScope,
        activeOrder: activeOrder,
        setActiveOrder: handleSetActiveOrder,
        checkoutItems,
        setCheckoutItems,
        // Cart sync functionality
        isCartSyncing,
        hasStaleItems,
        refreshCart,
        // Heating
        heatingOption,
        setHeatingOption,
        extraLogos,
        setExtraLogos,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

function useCartContext(): OrderBasket {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCartContext must be used within a CartProvider");
  }
  return context;
}

export { CartProvider, useCartContext };
